import type { AxiosResponse, InternalAxiosRequestConfig } from "axios";

import { getPreviewGraph } from "./graph";
import { PREVIEW_ENV_ID } from "./runtime";

export type WriteKind =
  | "create_update_funnel"
  | "delete_funnel"
  | "set_dynamic_events"
  | "admin_seed_conversion_events"
  | "create_survey"
  | "update_survey"
  | "delete_survey"
  | "publish_survey"
  | "pause_survey"
  | "unpublish_survey"
  | "ehr_marketing_analysis_activate_destination";

const WRITE_KINDS: ReadonlySet<WriteKind> = new Set<WriteKind>([
  "create_update_funnel",
  "delete_funnel",
  "set_dynamic_events",
  "admin_seed_conversion_events",
  "create_survey",
  "update_survey",
  "delete_survey",
  "publish_survey",
  "pause_survey",
  "unpublish_survey",
  "ehr_marketing_analysis_activate_destination",
]);

export function isWriteKind(kind: string): kind is WriteKind {
  return WRITE_KINDS.has(kind as WriteKind);
}

function previewFunnelEcho(request: unknown): unknown {
  // Stamps a synthetic id when missing — the create flow sends id: null.
  const f = (request ?? {}) as Record<string, unknown>;
  const id =
    (f.id as string | null | undefined) ?? `preview-funnel-${Date.now()}`;
  return { ...f, id };
}

type StatusOverride =
  | "active" // publish_survey
  | "paused" // pause_survey
  | "draft"; // unpublish_survey

function previewSurveyEcho(
  request: unknown,
  statusOverride?: StatusOverride,
): unknown {
  const r = (request ?? {}) as Record<string, unknown>;
  const surveyId =
    (r.surveyId as string | undefined) ?? `preview-survey-${Date.now()}`;
  const now = new Date().toISOString();

  // For status-only mutations (publish/archive/unpublish) the request
  // doesn't carry the full survey, so look up the existing one in the
  // synthetic graph and merge the new status into it. That gives the
  // optimistic-update consumer a coherent object instead of a stub.
  if (statusOverride) {
    const existing = getPreviewGraph().surveys.find((s) => s.id === surveyId);
    if (existing) {
      return {
        survey: {
          id: existing.id,
          envId: PREVIEW_ENV_ID,
          name: existing.name,
          description: existing.description,
          status: statusOverride,
          version: 1,
          trigger: {
            mode: existing.trigger.mode,
            matchType: existing.trigger.matchType,
            patterns: [...existing.trigger.patterns],
            eventNames: [...existing.trigger.eventNames],
          },
          idleTimeSeconds: existing.idleTimeSeconds,
          startDate: existing.startDate
            ? new Date(existing.startDate).toISOString()
            : null,
          endDate: existing.endDate
            ? new Date(existing.endDate).toISOString()
            : null,
          appearance: existing.appearance,
          questions: [],
          responseCount: 0,
          createdAt: new Date(existing.createdAt).toISOString(),
          updatedAt: now,
          sourceConversationId: null,
        },
      };
    }
  }

  const status = statusOverride ?? (r.status as string | undefined) ?? "draft";
  const trigger = (r.trigger ?? {}) as Record<string, unknown>;
  return {
    survey: {
      id: surveyId,
      envId: PREVIEW_ENV_ID,
      name: (r.name as string) ?? "",
      description: (r.description as string) ?? "",
      status,
      version: 1,
      trigger: {
        mode: (trigger.mode as "page" | "event" | undefined) ?? "page",
        matchType: (trigger.matchType as string | undefined) ?? "contains",
        patterns: Array.isArray(trigger.patterns)
          ? (trigger.patterns as string[])
          : [],
        eventNames: Array.isArray(trigger.eventNames)
          ? (trigger.eventNames as string[])
          : [],
      },
      idleTimeSeconds: (r.idleTimeSeconds as number) ?? 0,
      startDate: (r.startDate as string | null) ?? null,
      endDate: (r.endDate as string | null) ?? null,
      appearance: (r.appearance as unknown) ?? null,
      questions: Array.isArray(r.questions)
        ? (r.questions as Array<Record<string, unknown>>).map((q, i) => ({
            id: (q.id as string) ?? `preview-question-${i}`,
            surveyId,
            position: i,
            type: q.type,
            text: (q.text as string) ?? "",
            options: q.options ?? { type: q.type },
            createdAt: now,
            updatedAt: now,
          }))
        : [],
      responseCount: 0,
      createdAt: now,
      updatedAt: now,
      sourceConversationId: null,
    },
  };
}

export function emptyWriteResponse<T>(kind: WriteKind, request?: unknown): T {
  switch (kind) {
    case "create_update_funnel":
      return previewFunnelEcho(request) as T;
    case "delete_funnel":
    case "delete_survey":
      return { success: true } as T;
    case "set_dynamic_events":
      return {} as T;
    case "admin_seed_conversion_events":
      // Preview mode never seeds real events, so echo a no-op result rather
      // than a fabricated seeded list.
      return {
        envId: PREVIEW_ENV_ID,
        seeded: null,
        noOp: true,
        eligibility: "no_candidates",
      } as T;
    case "create_survey":
    case "update_survey":
      return previewSurveyEcho(request) as T;
    case "publish_survey":
      return previewSurveyEcho(request, "active") as T;
    case "pause_survey":
      return previewSurveyEcho(request, "paused") as T;
    case "unpublish_survey":
      return previewSurveyEcho(request, "draft") as T;
    case "ehr_marketing_analysis_activate_destination":
      return { success: true } as T;
  }
}

export function wrapPreviewResponse<T>(data: T): AxiosResponse<T> {
  return {
    data,
    status: 200,
    statusText: "OK",
    headers: {},
    config: {} as InternalAxiosRequestConfig,
  };
}
